import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react'
import { StepperControls } from '../../../components/StepperTypes/StepperTypes'
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import { GenericInputAction } from '../../../components/UI/GenericInputAction/GenericInputAction'
import { LoadingButton } from '../../../components/_EOS/Button/LoadingButton'
import { ClearAdornment } from '../../../components/UI/Adornments/ClearAdornment'
import { PreviousStep } from './PreviousStep'
import { InnerNotification } from './InnerNotification'
import { CheckCircle, Error, Undo, Visibility } from '@mui/icons-material'
import {
  AsnBlogCreate,
  AsnDetailReceiveLpnInfo,
  AsnDetailReceiveLpnPatch,
  AsnToReceive,
  IInnerNotification
} from '../Interfaces'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { getAsnToReceiveById, patchReceivedLPN, postAsnBlog } from '../api'
import { HistoryLpnReceived } from './HistoryLpnReceived'
import { eAsnState } from '../../../enums/eAsnState'
import { eTypeStorage } from '../../../enums/eTypeStorage'
import { Modal } from '../../../components/_EOS/Modal'
import { ModalAsnComment } from '../../../components/ASN/AsnViewer/ModalAsn/ModalAsnComment'
import { useModal } from '../../../hooks/useModal'

interface ReceiveLpnProps {
  controls: StepperControls
  openSnackBar: (msg: string | undefined, status: boolean) => void
  revertReceivedLpn?: boolean
}

export const ReceiveLpn: React.FC<ReceiveLpnProps> = ({
  controls,
  openSnackBar,
  revertReceivedLpn
}) => {
  const [comment, setComment] = useState<string>('')
  const { callApi, state } = useApi()
  const [selectedASN, setSelectedASN] = useState<AsnToReceive>()
  const [loading, setLoading] = useState(true)
  const [lpn, setLpn] = useState('')
  const [lpnReceived, setlpnReceived] = useState<AsnDetailReceiveLpnInfo>()
  const [notificationProps, setNotificationProps] =
    useState<IInnerNotification>()
  const [showHistory, setShowHistory] = useState(false)
  const [asnBlog, setAsnBlog] = useState('')
  const [asnStateId, setAsnStateId] = useState(0)
  const lpnInputRef = useRef<HTMLInputElement>(null)
  const requestAsnToReceive = async () => {
    callApi(getAsnToReceiveById(controls.currentState as number))
      .then((response: AsnToReceive) => {
        setSelectedASN(response)
        setAsnStateId(response.asnStateId)
        setLoading(false)
      })
      .catch((error: any) => {
        openSnackBar(error, false)
      })
  }

  useEffect(() => {
    requestAsnToReceive()
  }, [])

  useEffect(() => {
    if (!loading && !showHistory && lpnInputRef.current) {
      lpnInputRef.current.focus()
    }
  }, [loading, showHistory])

  const commentModalState = useModal<{
    id: number
  }>('')

  const buildInnerNotificationProps = (
    message?: string,
    icon?: ReactElement,
    textColor?: string,
    bgColor?: string,
    paragraphSize?: number
  ) => {
    setNotificationProps({
      message: message,
      icon: icon,
      textColor: textColor,
      bgColor: bgColor,
      paragraphSize: paragraphSize
    })
  }

  const requestToReserve = () => {
    requestPatchReceiveLPN(true)
  }

  const requestPatchReceiveLPN = (isReserve?: boolean) => {
    const body: AsnDetailReceiveLpnPatch = {
      lpn: lpn,
      receive: !revertReceivedLpn,
      isReserve: isReserve || false,
      comment: comment
    }
    callApi(patchReceivedLPN(selectedASN?.asnId || 0, body))
      .then((response: AsnDetailReceiveLpnInfo) => {
        setlpnReceived(response)
        setAsnStateId(response.asnStateId)
        setLpn('')
        buildInnerNotificationProps(
          response.asnStateId === eAsnState.Controlled
            ? `ASN ${selectedASN?.asnNumber} RECEPCIONADA CON ÉXITO`
            : `${response.lpn} - OK`,
          revertReceivedLpn ? <Undo /> : <CheckCircle />,
          'info.contrastText',
          'success.main',
          2
        )
      })
      .catch((error: any) => {
        buildInnerNotificationProps(
          `${lpn} - ${error}`,
          <Error />,
          'info.contrastText',
          error === 'Duplicado.' ? 'warning.main' : 'error.main',
          2
        )
      })
      .finally(() => {
        setLpn('')
      })
  }

  const requestPostAsnBlog = () => {
    const body: AsnBlogCreate = {
      asnId: selectedASN?.asnId || 0,
      comments: asnBlog
    }
    callApi(postAsnBlog(body))
      .then(() => {
        setLoading(false)
        openSnackBar(
          `ASN: ${selectedASN?.asnNumber} - Bitácora registrada correctamente.`,
          true
        )
        controls.goToInitialStep()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const isDisabledButton =
    lpn.length === 0 ||
    state.status === Status.Loading ||
    selectedASN?.asnStateId === eAsnState.Controlled

  const moreOptionsDisabledButton = state.status === Status.Loading

  const finishDisabledButton =
    state.status === Status.Loading || asnBlog.trim().length === 0

  const adornment = (
    <ClearAdornment
      onClick={() => {
        setLpn('')
      }}
      disabled={false}
    />
  )

  const isExterno = selectedASN?.typeStorageId === eTypeStorage.Externo

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isDisabledButton) {
      requestPatchReceiveLPN()
    }
  }

  const isInputDisabled =
    state.status === Status.Loading ||
    lpnReceived?.asnStateId === eAsnState.Controlled

  return (
    <Fragment>
      {!showHistory && <PreviousStep controls={controls} />}
      <Button
        style={{ marginLeft: '85%' }}
        onClick={() => setShowHistory(!showHistory)}
      >
        <Visibility color='primary' />
      </Button>
      {loading ? (
        <CircularProgress />
      ) : (
        <Fragment>
          <div style={{ display: showHistory ? 'none' : 'block' }}>
            <Typography
              variant='h6'
              style={{
                fontSize: '20px',
                fontWeight: 'bold'
              }}
            >{`Recepcionados ${
              lpnReceived?.totalLpnReceived || selectedASN?.lpnReceivedQty
            } de ${selectedASN?.lpnQty}`}</Typography>
            <Box
              component='div'
              display='flex'
              flexDirection='column'
              flexWrap='nowrap'
              alignItems='left'
              padding='10px 10px 0px 7px'
            >
              <Box>
                <Typography style={{ fontWeight: 'bold' }}>
                  ASN: {selectedASN?.asnNumber}
                </Typography>
                <GenericInputAction
                  name='ubication'
                  id='ubication'
                  onChange={(e) => {
                    setLpn(e.target.value)
                  }}
                  onKeyPress={handleInputKeyPress}
                  placeholder='Ingresar LPN'
                  value={lpn}
                  adornment={adornment}
                  status={'default'}
                  error={false}
                  errorText={''}
                  disabled={isInputDisabled}
                  inputRef={lpnInputRef}
                />
                {!revertReceivedLpn && isExterno && (
                  <LoadingButton
                    type='submit'
                    fullWidth
                    style={{
                      marginTop: '20px',
                      width: '90%',
                      minWidth: '90%',
                      left: '10px'
                    }}
                    disabled={isDisabledButton}
                    loading={state.status === Status.Loading}
                    onClick={() =>
                      commentModalState.openModal(
                        'sm',
                        { id: selectedASN?.asnId || 0 },
                        'Recepción a Reserva'
                      )
                    }
                  >
                    Almacenar En Reserva
                  </LoadingButton>
                )}
                <LoadingButton
                  type='submit'
                  fullWidth
                  style={{
                    marginTop: '20px',
                    width: '90%',
                    minWidth: '90%',
                    left: '10px'
                  }}
                  disabled={isDisabledButton}
                  loading={state.status === Status.Loading}
                  onClick={() => requestPatchReceiveLPN()}
                >
                  {revertReceivedLpn ? 'Revertir recepción' : 'Recibir'}
                </LoadingButton>
              </Box>
              {notificationProps && (
                <InnerNotification
                  message={notificationProps?.message || ''}
                  icon={notificationProps?.icon}
                  textColor={notificationProps?.textColor}
                  bgColor={notificationProps?.bgColor}
                  paragraphSize={notificationProps?.paragraphSize}
                ></InnerNotification>
              )}
              {lpnReceived?.asnStateId === eAsnState.Controlled && (
                <Fragment>
                  <TextField
                    multiline
                    rows={4}
                    label='Observaciones'
                    variant='outlined'
                    value={asnBlog}
                    onChange={(e) => {
                      setAsnBlog(e.target.value)
                    }}
                    required={true}
                    error={asnBlog.trim().length === 0}
                    helperText={
                      asnBlog.trim().length === 0
                        ? 'El campo Observaciones es requerido.'
                        : ''
                    }
                  />
                  <Button
                    fullWidth
                    variant='contained'
                    color='primary'
                    style={{
                      marginTop: '20px',
                      width: '95%',
                      minWidth: '95%'
                    }}
                    onClick={() => {
                      requestPostAsnBlog()
                    }}
                    disabled={finishDisabledButton}
                  >
                    Finalizar
                  </Button>
                </Fragment>
              )}
              {asnStateId === eAsnState.PreReception && !revertReceivedLpn && (
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  style={{
                    marginTop: '20px',
                    width: '95%',
                    minWidth: '95%'
                  }}
                  disabled={moreOptionsDisabledButton}
                  onClick={() => {
                    controls.goToNextStep(selectedASN?.asnId)
                  }}
                >
                  Más Opciones
                </Button>
              )}
            </Box>
          </div>
        </Fragment>
      )}
      {showHistory && (
        <HistoryLpnReceived
          asnNumber={selectedASN?.asnNumber}
          asnId={selectedASN?.asnId}
          openSnackBar={openSnackBar}
        />
      )}
      <Modal
        onClose={commentModalState.closeModal}
        open={commentModalState.isOpen}
        maxWidth={commentModalState.size}
        title={commentModalState.title}
      >
        <ModalAsnComment
          handleCloseModal={commentModalState.closeModal}
          requestCommentAsn={requestToReserve}
          setComment={setComment}
          comment={comment}
        />
      </Modal>
    </Fragment>
  )
}
